import { defineComponent as _defineComponent } from 'vue'
import BCXConfirm from '@/components/molecules/BCXConfirm.vue';
import Vue, {
  computed, ref, watch, del, Ref
} from 'vue';

import MarvinAlarm from '@/components/svg/marvin/marvin_alarm.svg?inline';
import MarvinHappy from '@/components/svg/marvin/marvin_happy.svg?inline';
import MarvinLookDownRight from '@/components/svg/marvin/marvin_look_down_right.svg?inline';

import useI18n from '@/mixins/useI18n';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import useInvoice from '@/views/my-projects/composables/useInvoice';
import { useRouter, useRoute } from 'vue2-helpers/vue-router';
import BCXForm from '@/components/molecules/forms/BCXForm.vue';
import { BcxFormField, BcxFormFieldErrorMessage } from '@/models/BcxForm';
import BCXRadio from '@/components/molecules/forms/BCXRadio.vue';
import translations from '@/locales/de/invoices/invoices.yaml';
import {
  InvoiceType, PayloadCancel, TypeOfPopUp, Invoice, ComponentInvoice, InvoiceEventPayload, InvoiceViolation
} from '@/models/Invoice';
import useBcxForm from '@/mixins/useBcxForm';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectInvoiceConfirms',
  setup(__props) {

const {
  events,
  fetchSendInvoice,
  fetchInvoices,
  fetchPaidInvoice,
  fetchDeleteInvoice,
  fetchInvoice,
  fetchCancelInvoice,
  fetchExtendInvoice,
  errorMessagesPositions,
  errorMessages,
  errorMessagesAddressDataSender,
  errorMessagesAddressDataReceiver,
  showConfirmInvoice,
  onConfirmInvoice,
  saveUnsavedChanges,
  unsavedAreas,
  resetUnsavedAreasIsProcessing,
  isSomethingUnsaved
} = useInvoice();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const typeOfPopUp = ref<TypeOfPopUp>('extend-due-date');
const okType = ref('warning');
const yesLabel = ref('yes');
const noLabel = ref('no');
const confirmText = ref('');
const justConfirmButton = ref(false);
const marvin = ref<any>(null);
const headLine = ref('');
const showRadios = ref(true);
const showNumberFiled = ref(false);
const showInvoiceNumberForCancellationInvoice = ref(false);
const showSimpleConfirm = ref(false);
const showNoButtons = ref(false);
const showSpinner = ref(false);
const isCheckPopup = computed(() => ['check-invoice', 'check-successfully'].includes(typeOfPopUp.value));

const invoice = ref<Invoice | ComponentInvoice | null>(null);
const invoiceId = computed(() => (invoice.value?.invoiceId ?? ''));
const isUserInInvoices = computed(() => route.path === '/my-projects/invoices');
const invoiceDocId = computed(() => invoice.value?.invoiceDocId || '');

const correctionOrCancellation = ref<'correction' | 'cancellation'>('cancellation');
const fieldData = ref({
  days: '7',
  cancellationNumber: ''
});
const fields = computed<BcxFormField[]>(() => {
  const numberField: BcxFormField[] = [
    {
      name: 'days',
      type: 'number',
      label: '',
      width: '100%',
      suffix: `${t('invoice.confirm-pop-up.form.days')}`,
      fieldWidth: '44%',
      required: true,
      textOptions: {
        max: 30,
        min: 3,
      },
    },
  ];

  const inputField: BcxFormField[] = [
    {
      name: 'cancellationNumber',
      type: 'text',
      label: '',
      width: '100%',
      fieldWidth: '100%',
      required: true,
      textOptions: {
        max: 30,
        min: 3,
      },
    },
  ];

  const result: BcxFormField[] = [];
  if (showNumberFiled.value) result.push(...numberField);
  if (showInvoiceNumberForCancellationInvoice.value) result.push(...inputField);

  return result;
});

const {
  errorMessages: errorMessagesDialog,
  hasErrors,
  validateAllFields,
} = useBcxForm(fieldData as unknown as Ref<Record<string, string>>, fields);

function resetSettings() {
  justConfirmButton.value = false;
  showRadios.value = false;
  showNumberFiled.value = false;
  showInvoiceNumberForCancellationInvoice.value = false;
  showSimpleConfirm.value = false;
  showNoButtons.value = false;
  showSpinner.value = false;
}

/**
 * Resets the form data for the confirmation dialog to their default values.
 * - Sets the number of days to '7' for extending the due date.
 * - Sets the cancellation invoice number to the current invoice's cancellation number or an empty string.
 * - Sets the correction or cancellation option to 'cancellation'.
 */
function resetData() {
  fieldData.value.days = '7';
  fieldData.value.cancellationNumber = invoice.value?.invoiceCancelDocId || '';
  correctionOrCancellation.value = 'cancellation';
  errorMessagesDialog.value = {};
}

/**
 * Opens the confirmation popup.
 */
function openPopup() {
  showConfirmInvoice.value = true;
}

/**
 * Closes the confirmation popup.
 */
function closePopup() {
  showConfirmInvoice.value = false;
  resetData();
  if (typeOfPopUp.value === 'unsaved-data') onConfirmInvoice(true);
  if (typeOfPopUp.value === 'invoice-has-been-sent') router.push({ name: 'myProjectsInvoices' });
}

/**
 * Deletes the current invoice and navigates to the invoice overview.
*/
function deleteInvoice() {
  fetchDeleteInvoice()
    .then(() => {
      closePopup();
      router.push({ name: 'myProjectsInvoices' });
    });
}

/**
 * Sends the invoice and updates state.
 */
async function sendInvoice() {
  events.emit('invoice-is-creating', { invoice: invoice.value });
  await fetchSendInvoice()
    .then(async () => {
      events.emit('invoice-has-been-sent', { invoice: invoice.value });
      await fetchInvoice();
    })
    .catch(() => {
      closePopup();
    });
}

/**
 * Deletes all keys from a given object using Vue's del (reactive delete).
 * @param obj - The object to clear
 */
function clearObject(obj: Record<string, unknown>) {
  Object.keys(obj).forEach((key) => {
    del(obj, key);
  });
}

/**
 * Resets all error message objects by clearing their properties.
 */
function resetAllErrorMessages() {
  errorMessagesPositions.value.forEach(clearObject);

  [
    errorMessages.value,
    errorMessagesAddressDataReceiver.value,
    errorMessagesAddressDataSender.value,
  ].forEach(clearObject);
}

/**
 * Creates a warning message if invoice items have zero cost
 * or if the performance period is incomplete.
 */
function createCheckMessage(): string {
  let checkMessage = '';
  const areItemsWithZero = invoice.value?.items.some((item) => item.costSingle === 0);

  if (areItemsWithZero) {
    checkMessage += `${t('invoice.confirm-texts.pleaseCheck')}\n`;
    checkMessage += `* ${t('invoice.confirm-texts.invoiceItemWithZero')} ${t(`invoice.payment-data.currencyOptions.${invoice.value?.account.currency}`)}\n`;
  }
  return checkMessage;
}

/**
 * Creates a success message after invoice validation,
 * including any additional check warnings if present.
 */
function createCheckSuccessfullyText(): string {
  let message = `${t('invoice.confirm-texts.check-successfully')}\n`;
  message += createCheckMessage();
  return message;
}

/**
 * Recursively finds all dot-separated paths to a given key in a nested object.
 *
 * @param obj - The object to search.
 * @param searchKey - The key to find.
 * @param path - Internal use for recursion; do not set manually.
 * @returns An array of dot-separated paths where the key was found.
 */
function findPathsByKey(obj: any, searchKey: string, path: string[] = []): string[] {
  let result: string[] = [];
  Object.entries(obj).forEach(([key, value]) => {
    const newPath = [...path, key];
    if (key === searchKey) {
      result.push(newPath.join('.'));
    }
    if (typeof value === 'object' && value !== null) {
      result = result.concat(findPathsByKey(value, searchKey, newPath));
    }
  });
  return result;
}

/**
 * Finds the most appropriate translation path for a given field key.
 *
 * Searches all matching keys in the `translations` object and returns the first match,
 * preferring paths that include 'form' if multiple matches are found.
 *
 * @param mandatoryField - The field path as an array.
 * @returns A dot-separated translation path string.
 */
function getTranslationPath(mandatoryField: string[]):string {
  const key = mandatoryField.at(-1) || '';
  const translationPathArray = findPathsByKey(translations, key);
  const [firstPath] = translationPathArray;

  let translationPath = '';
  if (translationPathArray.length === 1) translationPath = firstPath;
  else if (translationPathArray.length > 1) translationPath = translationPathArray.find((str) => str.includes('form')) || firstPath;

  return translationPath || '';
}

/**
 * Resolves the final field name for error assignment.
 * Handles combined fields (e.g. city & postal code) and special company case.
 *
 * @param field - The last part of the field path
 * @param mandatoryField - The array describing the field path
 * @returns The resolved field name as string
 */
function resolveFieldName(field: string, mandatoryField: string[]): string {
  if (field === 'city' || field === 'postalCode') return 'zipAndCity';
  if (mandatoryField[2] === 'company' && mandatoryField[3] === 'name') return 'company';
  return field;
}

/**
 * Sets a field error message on the appropriate error object,
 * depending on the provided path.
 *
 * @param mandatoryField - Array describing the field path (e.g. ['items', '0', 'name'])
 */
function createErrorMessages(mandatoryField: string[]) {
  const field = mandatoryField.at(-1) || '';
  const errorObjectTemplate: BcxFormFieldErrorMessage = {
    isVisible: true,
    message: `${t('common.bcxFormValidations.required')}`,
  };

  // Special case: Position fields ("items")
  if (mandatoryField[0] === 'items' && mandatoryField.length >= 2) {
    const index = Number(mandatoryField[1]);
    if (errorMessagesPositions.value[index]) {
      Vue.set(errorMessagesPositions.value[index], field, { ...errorObjectTemplate });
    }
    return;
  }

  // Sender address fields
  if (mandatoryField[0] === 'sender') {
    const resolvedField = resolveFieldName(field, mandatoryField);
    Vue.set(errorMessagesAddressDataSender.value, resolvedField, { ...errorObjectTemplate });
    return;
  }

  // Receiver address fields
  if (mandatoryField[0] === 'receiver') {
    const resolvedField = resolveFieldName(field, mandatoryField);
    Vue.set(errorMessagesAddressDataReceiver.value, resolvedField, { ...errorObjectTemplate });
    return;
  }

  // Default case: Assign error to the root errorMessages object
  Vue.set(errorMessages.value, field, { ...errorObjectTemplate });
}

/**
 * Builds a localized validation message for a missing or invalid invoice field.
 *
 * Determines the correct translation key and message format based on the
 * structure and content of the mandatory field path. Handles special cases
 * such as invoice positions, sender/receiver addresses, performance periods,
 * and duplicate invoice number violations. The resulting message is formatted
 * as a bullet point and always ends with a line break.
 */
function createMessage(mandatoryField: string[], violation?: InvoiceViolation): string {
  if (mandatoryField[0] === 'items' && mandatoryField.length === 1) return `* ${t('invoice.positions.title')}\n`;
  if (mandatoryField[0] === 'performancePeriodTo') return `* ${t('invoice.basis-data.form.performancePeriodToWrittenOut')}\n`;
  if (mandatoryField[2] === 'company' && mandatoryField[3] === 'name') mandatoryField[3] = 'company';
  if (mandatoryField[0] === 'invoiceDocId' && violation?.reason === 'DUPLICATE_CHECK') return `* ${t('invoice.payment-data.error-texts.duplicate-invoice-number')}\n`;
  if (mandatoryField[0] === 'dueDate' && violation?.reason === 'INVALID_DATE_CHECK') return `* ${t('invoice.payment-data.error-texts.payment-overdue')}\n`;
  const translationPath = getTranslationPath(mandatoryField);

  if (mandatoryField[0] === 'items' && mandatoryField.length > 1) {
    return `* ${t(translationPath)} ${t('invoice.positions.form.position')} ${!Number.isNaN(Number(mandatoryField[1])) ? Number(mandatoryField[1]) + 1 : ''}\n`;
  }
  if ((mandatoryField[0] === 'sender' || mandatoryField[0] === 'receiver') && mandatoryField.length > 1) {
    if (mandatoryField[0] === 'sender') return `* ${t(translationPath)} ${t('invoice.address-data.headlineSender')}\n`;
    if (mandatoryField[0] === 'receiver') return `* ${t(translationPath)} ${t('invoice.address-data.headlineRecipient')}\n`;
  }

  return `* ${t(translationPath)}\n`;
}

/**
 * Sorts invoice violations into a consistent, user-friendly order.
 *
 * Violations are grouped by field category priority (sender, receiver, account,
 * intro, other fields, items). Item-related violations are further ordered by
 * their item index and field path. The input array is not mutated.
 */
function sortViolations(violations: InvoiceViolation[]) {
  const getItemIndex = (str: string) => {
    const match = str.match(/items\[(\d+)/);
    return match ? parseInt(match[1], 10) : -1;
  };

  return violations.slice().sort((a, b) => {
    const priority = (key: string) => {
      if (key.startsWith('sender')) return 1;
      if (key.startsWith('receiver')) return 2;
      if (key.startsWith('account')) return 3;
      if (key === 'intro') return 5;
      if (key.startsWith('items')) return 6;
      return 4;
    };

    const pa = priority(a.fieldPath);
    const pb = priority(b.fieldPath);
    if (pa !== pb) return pa - pb;

    if (pa === 6) {
      const ia = getItemIndex(a.fieldPath);
      const ib = getItemIndex(b.fieldPath);
      if (ia !== ib) return ia - ib;
    }

    return a.fieldPath.localeCompare(b.fieldPath);
  });
}

/**
 * Creates the confirmation text shown when sending an invoice fails.
 *
 * Starts with a generic send-error message, then processes all relevant
 * invoice violations by filtering, sorting, and resetting existing error
 * messages. For each violation, the field path is normalized into an array,
 * error messages are generated, and a localized, formatted message line is
 * appended. Returns the complete confirmation text as a single string.
 */
function createConfirmText(violations: InvoiceViolation[]): string {
  let message = `${t('invoice.confirm-texts.send-error')}`;
  const filteredViolations = violations.filter((violation) => violation.reason === 'MANDATORY_CHECK' || 'DUPLICATE_CHECK' || 'INVALID_DATE_CHECK');
  const sortedViolations = sortViolations(filteredViolations);
  resetAllErrorMessages();

  sortedViolations.forEach((violation) => {
    const violationPathAsArray = violation.fieldPath.replace(/\[(\d+)]/g, '.$1').split('.');
    createErrorMessages(violationPathAsArray);
    message += createMessage(violationPathAsArray, violation);
  });

  return message;
}

function createCheckConfirmText(violations: InvoiceViolation[]): string {
  let message = createConfirmText(violations);
  message += createCheckMessage();

  return message;
}

/**
 * Handles marking an invoice as paid.
 * Determines the correct invoice number based on the current route,
 * fetches the paid invoice, then refreshes invoice data and closes the popup.
 */
function paidInvoice() {
  fetchPaidInvoice(0, invoiceId.value || '')
    .then(async () => {
      if (!isUserInInvoices.value) await fetchInvoice();
      else await fetchInvoices();
      closePopup();
    });
}

/**
 * Extends invoice due date by given days and refreshes data.
 */
function extendInvoice() {
  validateAllFields();
  if (hasErrors.value) return;

  const payload = {
    expansionDays: Number(fieldData.value.days),
  };

  fetchExtendInvoice(0, payload, invoiceId.value)
    .then(async () => {
      if (!isUserInInvoices.value) await fetchInvoice();
      else await fetchInvoices();
      resetData();
      closePopup();
    });
}

/**
 * Emits an event for correction or cancellation based on user selection.
 */
function correctionOrCancellationInvoice() {
  if (correctionOrCancellation.value === 'correction') {
    events.emit('correction-invoice', { invoice: invoice.value });
  } else if (correctionOrCancellation.value === 'cancellation') {
    events.emit('cancellation-invoice', { invoice: invoice.value });
  }
}

/**
 * Cancels or corrects the invoice based on type.
 * Emits confirmation event after API call.
 * @param cancelType - 'CORRECTION' or 'CANCEL'
 */
function cancelInvoice(cancelType: InvoiceType) {
  validateAllFields();
  if (hasErrors.value) return;

  const payload: PayloadCancel = {
    cancelType,
    invoiceDocId: fieldData.value.cancellationNumber,
  };

  if (cancelType === 'CANCEL') events.emit('cancellation-is-creating', { invoice: invoice.value });
  else if (cancelType === 'CORRECTION') events.emit('correction-is-creating', { invoice: invoice.value });

  fetchCancelInvoice(0, payload, invoiceId.value)
    .then(async (data) => {
      resetData();
      switch (cancelType) {
        case 'CORRECTION':
          events.emit('correction-confirmation-invoice', { invoice: data } as InvoiceEventPayload);
          break;
        case 'CANCEL':
          events.emit('cancellation-confirmation-invoice', { invoice: invoice.value });
          break;
      }
    })
    .catch(() => {
      closePopup();
    });
}

function correctionSent() {
  closePopup();
  router.push(`/my-projects/invoice/${invoice.value?.invoiceId}`);
}

/**
 * Navigates to the invoice overview page after a cancellation has been confirmed.
 */
function cancellationSent() {
  router.push({ name: 'myProjectsInvoices' });
  closePopup();
}

/**
 * Waits until all processing flags are false.
 * @returns {Promise<void>} Resolves when address/payment/positions stop processing.
 */
async function waitUntilAllProcessingFalse(): Promise<void> {
  return new Promise<void>((resolve) => {
    const allFalse = () => unsavedAreas.value.addressData.isProcessing === false
      && unsavedAreas.value.paymentData.isProcessing === false
      && unsavedAreas.value.positionsData.isProcessing === false;

    if (allFalse()) {
      resolve();
      return;
    }

    const stop = watch(
      () => [
        unsavedAreas.value.addressData.isProcessing,
        unsavedAreas.value.paymentData.isProcessing,
        unsavedAreas.value.positionsData.isProcessing
      ],
      () => {
        if (allFalse()) {
          stop();
          resolve();
        }
      },
      { immediate: true }
    );
  });
}

/**
 * Handles unsaved data flow: save/cancel, wait if needed, confirm and close.
 * @param {boolean} cancelOrConfirm - True to save/confirm, false to discard.
 */
async function onUnsavedData(cancelOrConfirm: boolean) {
  saveUnsavedChanges.value = cancelOrConfirm ? 'save' : 'doNotSave';
  resetUnsavedAreasIsProcessing();
  if (cancelOrConfirm) {
    await waitUntilAllProcessingFalse();
  }
  if (saveUnsavedChanges.value === 'save' && isSomethingUnsaved.value) {
    events.emit('unsaved-data-failed', { invoice: invoice.value });
  } else closePopup();
}

/**
 * Handles approval or cancellation in the confirmation dialog.
 * Executes actions based on popup type and user choice.
 * @param cancelOrConfirm - true if approved, false if cancelled
 */
async function onApprove(cancelOrConfirm: boolean) {
  switch (typeOfPopUp.value) {
    case 'send-invoice':
      if (cancelOrConfirm) await sendInvoice();
      else closePopup();
      break;
    case 'invoice-has-been-sent':
      closePopup();
      break;
    case 'invoice-missing-fields':
      closePopup();
      break;
    case 'paid-invoice':
      if (cancelOrConfirm) paidInvoice();
      else closePopup();
      break;
    case 'delete-invoice':
      if (cancelOrConfirm) deleteInvoice();
      else closePopup();
      break;
    case 'check-successfully':
      closePopup();
      break;
    case 'correction-or-cancellation-invoice':
      if (cancelOrConfirm) correctionOrCancellationInvoice();
      else {
        closePopup();
        resetData();
      }
      break;
    case 'extend-due-date':
      if (cancelOrConfirm) {
        extendInvoice();
      } else {
        resetData();
        closePopup();
      }
      break;
    case 'correction-invoice':
      if (cancelOrConfirm) {
        cancelInvoice('CORRECTION');
      } else {
        events.emit('correction-or-cancellation-invoice', { invoice: invoice.value });
      }
      break;
    case 'cancellation-invoice':
      if (cancelOrConfirm) {
        cancelInvoice('CANCEL');
      } else {
        events.emit('correction-or-cancellation-invoice', { invoice: invoice.value });
      }
      break;
    case 'correction-confirmation-invoice':
      correctionSent();
      break;
    case 'cancellation-confirmation-invoice':
      cancellationSent();
      break;
    case 'unsaved-data':
      await onUnsavedData(cancelOrConfirm);
      break;
    case 'unsaved-data-failed':
      closePopup();
      break;
    case 'check-invoice':
      closePopup();
      break;
  }
}

function onCancel() {
  saveUnsavedChanges.value = 'cancel';
  closePopup();
}

events.on(async (evt: TypeOfPopUp, payload: InvoiceEventPayload) => {
  const { invoice: eventInvoice, invoiceViolation = [] } = payload;

  if (evt) typeOfPopUp.value = evt as TypeOfPopUp;
  if (eventInvoice) invoice.value = eventInvoice;
  resetSettings();

  switch (evt) {
    case 'send-invoice':
      okType.value = 'ok';
      yesLabel.value = `${t('invoice.buttons.send')}`;
      noLabel.value = `${t('buttons.no')}`;
      confirmText.value = `${t('invoice.confirm-texts.send')}`;
      marvin.value = MarvinHappy;
      showSimpleConfirm.value = true;
      break;
    case 'invoice-is-creating':
      okType.value = 'ok';
      confirmText.value = `${t('invoice.confirm-texts.invoice-is-creating')}`;
      marvin.value = MarvinHappy;
      showNoButtons.value = true;
      showSimpleConfirm.value = true;
      showSpinner.value = true;
      break;
    case 'correction-is-creating':
      okType.value = 'ok';
      confirmText.value = `${t('invoice.confirm-texts.correction-is-creating')}`;
      marvin.value = MarvinHappy;
      showNoButtons.value = true;
      showSimpleConfirm.value = true;
      showSpinner.value = true;
      break;
    case 'cancellation-is-creating':
      okType.value = 'ok';
      confirmText.value = `${t('invoice.confirm-texts.cancellation-is-creating')}`;
      marvin.value = MarvinHappy;
      showNoButtons.value = true;
      showSimpleConfirm.value = true;
      showSpinner.value = true;
      break;
    case 'invoice-has-been-sent':
      okType.value = 'ok';
      yesLabel.value = `${t('buttons.ok')}`;
      confirmText.value = `${t('invoice.confirm-texts.invoice-has-been-sent')}`;
      marvin.value = MarvinHappy;
      justConfirmButton.value = true;
      showSimpleConfirm.value = true;
      break;
    case 'invoice-missing-fields':
      okType.value = 'primary';
      yesLabel.value = `${t('buttons.ok')}`;
      confirmText.value = createConfirmText(invoiceViolation);
      marvin.value = MarvinAlarm;
      justConfirmButton.value = true;
      showSimpleConfirm.value = true;
      break;
    case 'paid-invoice':
      okType.value = 'ok';
      yesLabel.value = `${t('invoice.buttons.paidConfirm')}`;
      noLabel.value = `${t('buttons.no')}`;
      confirmText.value = `${t('invoice.confirm-texts.paid')}`;
      marvin.value = MarvinHappy;
      showSimpleConfirm.value = true;
      break;
    case 'delete-invoice':
      okType.value = 'warning';
      yesLabel.value = `${t('invoice.buttons.delete')}`;
      noLabel.value = `${t('buttons.no')}`;
      confirmText.value = `${t('invoice.confirm-texts.delete')}`;
      marvin.value = MarvinAlarm;
      showSimpleConfirm.value = true;
      break;
    case 'check-invoice':
      okType.value = 'primary';
      yesLabel.value = `${t('buttons.ok')}`;
      confirmText.value = createCheckConfirmText(invoiceViolation);
      marvin.value = MarvinAlarm;
      justConfirmButton.value = true;
      showSimpleConfirm.value = true;
      break;
    case 'check-successfully':
      okType.value = 'ok';
      yesLabel.value = `${t('buttons.ok')}`;
      confirmText.value = createCheckSuccessfullyText();
      marvin.value = MarvinHappy;
      justConfirmButton.value = true;
      showSimpleConfirm.value = true;
      break;
    case 'correction-or-cancellation-invoice':
      okType.value = 'primary';
      yesLabel.value = `${t('invoice.buttons.next')}`;
      noLabel.value = `${t('invoice.buttons.cancel')}`;
      confirmText.value = `${t('invoice.confirm-texts.correctionOrCancellation')}`;
      marvin.value = MarvinLookDownRight;
      headLine.value = `${t('invoice.buttons.correctionOrCancellation')}`;
      showRadios.value = true;
      break;
    case 'extend-due-date':
      okType.value = 'primary';
      yesLabel.value = `${t('invoice.buttons.extend')}`;
      noLabel.value = `${t('invoice.buttons.cancel')}`;
      confirmText.value = `${t('invoice.confirm-texts.extend')}`;
      marvin.value = MarvinLookDownRight;
      headLine.value = `${t('invoice.buttons.extend')}`;
      showNumberFiled.value = true;
      break;
    case 'correction-invoice':
      okType.value = 'ok';
      yesLabel.value = `${t('invoice.buttons.execute')}`;
      noLabel.value = `${t('invoice.buttons.back')}`;
      confirmText.value = `${t('invoice.confirm-texts.correction')}`;
      marvin.value = MarvinLookDownRight;
      headLine.value = `${t('invoice.buttons.correction')}`;
      showInvoiceNumberForCancellationInvoice.value = true;
      break;
    case 'cancellation-invoice':
      okType.value = 'ok';
      yesLabel.value = `${t('invoice.buttons.execute')}`;
      noLabel.value = `${t('invoice.buttons.back')}`;
      confirmText.value = `${t('invoice.confirm-texts.cancellation')}`;
      marvin.value = MarvinLookDownRight;
      headLine.value = `${t('invoice.buttons.cancellation')}`;
      showInvoiceNumberForCancellationInvoice.value = true;
      break;
    case 'correction-confirmation-invoice':
      okType.value = 'without';
      yesLabel.value = `${t('buttons.ok')}`;
      confirmText.value = `${t('invoice.confirm-texts.correction-sent')}`;
      marvin.value = MarvinHappy;
      justConfirmButton.value = true;
      showSimpleConfirm.value = true;
      break;
    case 'cancellation-confirmation-invoice':
      okType.value = 'without';
      yesLabel.value = `${t('buttons.ok')}`;
      confirmText.value = `${t('invoice.confirm-texts.cancellation-sent')}`;
      marvin.value = MarvinHappy;
      justConfirmButton.value = true;
      showSimpleConfirm.value = true;
      break;
    case 'unsaved-data':
      okType.value = 'primary';
      yesLabel.value = `${t('buttons.yes')}`;
      noLabel.value = `${t('buttons.no')}`;
      confirmText.value = `${t('invoice.confirm-texts.unsaved-data')}`;
      marvin.value = MarvinAlarm;
      showSimpleConfirm.value = true;
      break;
    case 'unsaved-data-failed':
      okType.value = 'primary';
      yesLabel.value = `${t('buttons.ok')}`;
      confirmText.value = `${t('invoice.confirm-texts.unsaved-data-failed')}`;
      marvin.value = MarvinAlarm;
      justConfirmButton.value = true;
      showSimpleConfirm.value = true;
      saveUnsavedChanges.value = 'doNotSave'; // To reset it
      break;
  }

  openPopup();
});

watch(
  () => invoice.value?.invoiceCancelDocId,
  (newVal) => {
    if (newVal) {
      fieldData.value.cancellationNumber = newVal;
    }
  },
  { immediate: true }
);

return { __sfc: true,events, fetchSendInvoice, fetchInvoices, fetchPaidInvoice, fetchDeleteInvoice, fetchInvoice, fetchCancelInvoice, fetchExtendInvoice, errorMessagesPositions, errorMessages, errorMessagesAddressDataSender, errorMessagesAddressDataReceiver, showConfirmInvoice, onConfirmInvoice, saveUnsavedChanges, unsavedAreas, resetUnsavedAreasIsProcessing, isSomethingUnsaved, t, router, route, typeOfPopUp, okType, yesLabel, noLabel, confirmText, justConfirmButton, marvin, headLine, showRadios, showNumberFiled, showInvoiceNumberForCancellationInvoice, showSimpleConfirm, showNoButtons, showSpinner, isCheckPopup, invoice, invoiceId, isUserInInvoices, invoiceDocId, correctionOrCancellation, fieldData, fields, errorMessagesDialog, hasErrors, validateAllFields, resetSettings, resetData, openPopup, closePopup, deleteInvoice, sendInvoice, clearObject, resetAllErrorMessages, createCheckMessage, createCheckSuccessfullyText, findPathsByKey, getTranslationPath, resolveFieldName, createErrorMessages, createMessage, sortViolations, createConfirmText, createCheckConfirmText, paidInvoice, extendInvoice, correctionOrCancellationInvoice, cancelInvoice, correctionSent, cancellationSent, waitUntilAllProcessingFalse, onUnsavedData, onApprove, onCancel, BCXConfirm, BCXMarkdown, BCXForm, BCXRadio }
}

})